/**
 * This component contains code to complete a service record,
 * this modal is only accessible when a pin is generated
 */
import React from "react";
import {
  Grid,
  TextField,
  Typography,
  CardHeader,
  Divider,
  DialogActions,
  Dialog,
  DialogContent,
  DialogTitle,
  InputLabel,
  MenuItem,
  IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import * as constants from "helpers/constants";
import DashedDivider from "common/ui/DashedDivider";
import { CompleteServiceRecordForm } from "./CompleteService.types";
import {
  useReasonForServiceType,
  useCompleteServiceRecord,
  useMachineSummary,
  useLocationType
} from "services/machines.service";
import { useTranslation } from "react-i18next";
import { Controller } from "react-hook-form";
import useForm from "hooks/useForm";
import { useQueryClient } from "@tanstack/react-query";
import { encode, toDate } from "utils/common";
import { toast } from "react-hot-toast";
import _ from "lodash";
import { useAppDispatch, useAppSelector } from "context/hooks";
import {
  setCompleteServiceRecord,
  setRequestSoftware
} from "context/modal.context";
import { selectCompleteServiceRecordProps } from "context/modal.selector";
import LoadingButton from "common/ui/LoadingButton";

function CompleteService() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const props = useAppSelector(selectCompleteServiceRecordProps);
  const machineDetails = useMachineSummary(props.machineId);
  const {
    handleSubmit,
    control,
    register,
    clearErrors,
    reset,
    formState: { errors }
  } = useForm<CompleteServiceRecordForm>({
    defaultValues: {
      serviceReasonId: "",
      serviceLocationId: "",
      comments: ""
    }
  });

  const queryClient = useQueryClient();

  const reasonForServiceType = useReasonForServiceType();

  const locationType = useLocationType();

  React.useEffect(() => {
    if (props.open) {
      reset({
        serviceReasonId: "",
        serviceLocationId: "",
        comments: ""
      });
    }
  }, [props.open, reset]);

  const serviceRecordId =
    machineDetails?.data?.machineSummary?.serviceRecordPin.serviceRecordId;

  const completeServiceRecord = useCompleteServiceRecord(
    onCompleteServiceRecordUpdate,
    serviceRecordId
  );

  async function onCompleteServiceRecordUpdate(_data, error) {
    await queryClient.invalidateQueries({
      queryKey: ["machine", "detail"],
      exact: false
    });
    await queryClient.invalidateQueries({
      queryKey: ["service-records"],
      exact: false
    });
    await queryClient.invalidateQueries({
      queryKey: ["latest-software"],
      exact: false
    });

    if (!error) {
      toast.success(t("completeService.serviceRecordAddedSuccessfully"));
    }
    dispatch(setRequestSoftware({ open: false, machineId: null }));
    dismiss();
  }

  function dismiss() {
    clearErrors();
    dispatch(setCompleteServiceRecord({ open: false, machineId: null }));
  }

  function onSubmit(data: CompleteServiceRecordForm) {
    completeServiceRecord.mutate({
      ...data,
      comments: encode(data.comments)
    });
  }

  return (
    <Dialog open={props.open} scroll="body">
      <DialogTitle
        component={CardHeader}
        avatar={<SettingsIcon />}
        title={t("completeService.completeServiceRecord")}
        titleTypographyProps={{ variant: "h6" }}
        color="primary"
        action={
          <IconButton onClick={dismiss}>
            <CloseIcon />
          </IconButton>
        }
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ py: 0 }}>
          <DashedDivider />
          <Grid container sx={{ color: "grey", my: 2, px: 3 }}>
            <Grid xs={6} item container justifyContent="space-between">
              <Typography variant="body1" alignSelf="center" noWrap>
                {t("completeService.machineSerialNumber")}
              </Typography>
              <Divider
                orientation="vertical"
                variant="middle"
                flexItem
                sx={{ borderColor: "gray.dark" }}
              />
            </Grid>
            <Grid xs={6} item>
              <Typography variant="subtitle1" fontWeight="bold" ml={5} noWrap>
                {_.defaultTo(
                  machineDetails?.data?.machineSummary.serialNumber,
                  ""
                )}
              </Typography>
            </Grid>
            <Grid xs={6} item container justifyContent="space-between">
              <Typography variant="body1" alignSelf="center" noWrap>
                {t("completeService.machineNameOrNumber")}
              </Typography>
              <Divider
                orientation="vertical"
                variant="middle"
                flexItem
                sx={{ borderColor: "gray.dark" }}
              />
            </Grid>
            <Grid xs={6} item>
              <Typography variant="subtitle1" fontWeight="bold" ml={5} noWrap>
                {_.defaultTo(machineDetails?.data?.machineSummary.name, "")}
              </Typography>
            </Grid>
            <Grid xs={6} item container justifyContent="space-between">
              <Typography variant="body1" alignSelf="center" noWrap>
                {t("completeService.serviceDue")}
              </Typography>
              <Divider
                orientation="vertical"
                variant="middle"
                flexItem
                sx={{ borderColor: "gray.dark" }}
              />
            </Grid>
            <Grid xs={6} item>
              <Typography variant="subtitle1" fontWeight="bold" ml={5} noWrap>
                {_.defaultTo(
                  toDate(
                    machineDetails?.data?.machineSummary.serviceRecordPin
                      .serviceRecordPinExpiry,
                    constants.DATE_TIME_FORMAT
                  ),
                  "-"
                )}
              </Typography>
            </Grid>
          </Grid>
          <DashedDivider />
          <Grid container>
            <Grid container item xs={12}>
              {reasonForServiceType.isSuccess && (
                <>
                  <InputLabel htmlFor="serviceReasonId" required>
                    {t("completeService.reasonForService")}
                  </InputLabel>
                  <Controller
                    control={control}
                    name="serviceReasonId"
                    rules={{
                      required: t("completeService.requiredSelect")
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        helperText={errors.serviceReasonId?.message}
                        error={!!errors?.serviceReasonId}
                        inputProps={{
                          id: "serviceReasonId",
                          "aria-label": "Reason for Service"
                        }}
                        select
                        size="small"
                        fullWidth
                        name="serviceReasonId"
                        SelectProps={{
                          displayEmpty: true,
                          MenuProps: { sx: { maxHeight: 300 } }
                        }}
                      >
                        <MenuItem value="">{t("common.select")}</MenuItem>
                        {reasonForServiceType.data?.map((item) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </>
              )}
            </Grid>
            <Grid container item xs={12}>
              {locationType.isSuccess && (
                <>
                  <InputLabel htmlFor="serviceLocationId" required>
                    {t("completeService.whereDidTheServiceTakePlace")}
                  </InputLabel>
                  <Controller
                    control={control}
                    name="serviceLocationId"
                    rules={{
                      required: t("completeService.requiredSelect")
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        inputProps={{
                          id: "serviceLocationId",
                          "aria-label": "Location of Service"
                        }}
                        select
                        size="small"
                        fullWidth
                        helperText={errors.serviceLocationId?.message}
                        error={!!errors.serviceLocationId}
                        name="serviceLocationId"
                        SelectProps={{
                          displayEmpty: true,
                          MenuProps: { sx: { maxHeight: 300 } }
                        }}
                      >
                        <MenuItem value="">{t("common.select")}</MenuItem>
                        {locationType.data?.map((item) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </>
              )}
            </Grid>
            <Grid container item xs={12}>
              <InputLabel htmlFor="comments" required>
                {t("completeService.comments")}
              </InputLabel>
              <TextField
                {...register("comments", {
                  required: t("completeService.required")
                })}
                helperText={errors.comments?.message}
                error={!!errors.comments}
                id="comments"
                size="small"
                fullWidth
                variant="outlined"
                rows={3}
                multiline={true}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", py: 2 }}>
          <LoadingButton
            loading={completeServiceRecord.isLoading}
            type="submit"
            variant="outlined"
          >
            {t("completeService.completeServiceRecord")}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default CompleteService;
